/** A/S 개인 관리 — 수리완료 리스트 다운로드(내부/외부) 엑셀 열 설정 */

export const AS_REPAIR_LIST_EXPORT_MODES = ['internal', 'external'];

export const AS_REPAIR_LIST_EXPORT_MODE_LABELS = {
    internal: '내부',
    external: '외부',
};

/** 수리완료 리스트 다운로드(내부) 기본 열 */
export const PERSONAL_REPAIR_COMPLETE_INTERNAL_HEADERS = [
    '박스번호',
    '모델명',
    '품목명',
    '옵션사항',
    '현장명',
    '불량명',
    '출고일',
    '발송일자',
    '전체수량',
    '수량부족',
    '실입고수량',
    '미완료수량',
    '반품제품',
    '수리기간초과',
    '불량명다름',
    '폐기제품',
    '외부압력',
    '패턴오픈',
    '수리완료수량',
    '유상수리수량',
    '정상수량',
    '사출스크레치',
    '플레이트스크레치',
    '이물질',
    'LCD파손',
    '다이얼파손',
    '온도센서파손',
    '커넥터파손',
    '케이블파손',
    'Micom파손',
    '미세먼지소음',
    '무상수리수량',
    '전원불량',
    'COIL간섭',
    'TRANS',
    'SMPS',
    '퓨즈교체',
    '전원IC교체',
    '터치불량',
    '터치IC교체',
    '터치가스켓없음',
    '터치가스켓눌림',
    '소리불량',
    '스피커교체',
    '스피커냉땜',
    '홈넷불량',
    '홈넷IC교체',
    '홈넷TR교체',
    '보일러불량',
    '보일러IC교체',
    '보일러TR교체',
    '에어컨불량',
    '에어컨IC교체',
    '에어컨TR교체',
    '서브PCB불량',
    '서브PCB분실',
    '서브PCB펌웨어',
    'Micom교체',
    'Micom고장',
    '먹통현상',
    '펌웨어업데이트',
    'LCD고장',
    '세그먼트불량',
    'LCD냉땜',
    '릴레이교체',
    '외부충격',
    '릴레이고장',
    '통신불량',
    '통신TR교체',
    '통신저항교체',
    '세팅불량',
    '핀설정불량',
    '점퍼분실',
    '커넥터교체',
    '홈넷커넥터',
    '조절기커넥터',
    '보일러커넥터',
    '에어컨커넥터',
    '타사커넥터',
    '작동정상',
    '리스트표기수정',
    '비고사항',
];

/** 수리완료 리스트 다운로드(외부) 기본 열 */
export const PERSONAL_REPAIR_COMPLETE_EXTERNAL_HEADERS = [
    '박스번호',
    '모델명',
    '품목명',
    '옵션사항',
    '현장명',
    '불량명',
    '출고일',
    '발송일자',
    '전체수량',
    '실입고수량',
    '수량부족',
    '미완료수량',
    '반품제품',
    '수리기간초과',
    '불량명다름',
    '폐기제품',
    '외부압력',
    '패턴오픈',
    '수리완료수량',
    '유상수리수량',
    '정상수량',
    '사출스크레치',
    '플레이트스크레치',
    '이물질',
    'LCD파손',
    '다이얼파손',
    '온도센서파손',
    '커넥터파손',
    '케이블파손',
    'Micom파손',
    '미세먼지소음',
    '리스트표기수정',
    '비고사항',
];

const DEFAULT_HEADERS_BY_MODE = {
    internal: PERSONAL_REPAIR_COMPLETE_INTERNAL_HEADERS,
    external: PERSONAL_REPAIR_COMPLETE_EXTERNAL_HEADERS,
};

const HEADER_ALIASES = {
    출고일: ['출고일', '출고일자'],
    비고사항: ['비고사항', '비고', 'memo', 'Memo'],
    COIL간섭: ['COIL간섭', 'COIL 간섭', 'Coil간섭'],
    사출스크레치: ['사출스크레치', '스크레치'],
    전원IC교체: ['전원IC교체', '7805고장'],
};

const normalizeHeaderToken = (value) => String(value || '')
    .replace(/\s+/g, '')
    .replace(/[·・]/g, '')
    .toLowerCase();

const getHeaderAliasList = (header) => {
    const aliases = HEADER_ALIASES[header];
    return aliases && aliases.length > 0 ? aliases : [header];
};

/** 엑셀 열 제목으로 A/S 필드 찾기 (라벨 일치 → 키 일치 → 포함 매칭) */
export const findAsRepairFieldByExportHeader = (fields, header, usedKeys = new Set()) => {
    const pool = fields.filter((f) => f?.key && !usedKeys.has(f.key));
    const aliasTokens = getHeaderAliasList(header).map(normalizeHeaderToken);

    for (const token of aliasTokens) {
        const exact = pool.find((f) => normalizeHeaderToken(f.label) === token);
        if (exact) {
            return exact;
        }
    }
    for (const token of aliasTokens) {
        const byKey = pool.find((f) => {
            const keyToken = normalizeHeaderToken(String(f.key || '').replace(/^custom_/i, ''));
            return keyToken === token || normalizeHeaderToken(f.key) === token;
        });
        if (byKey) {
            return byKey;
        }
    }
    // 짧은 라벨 오매칭 방지를 위해 긴 것 우선
    const scored = [];
    pool.forEach((field) => {
        const labelToken = normalizeHeaderToken(field.label);
        const keyToken = normalizeHeaderToken(String(field.key || '').replace(/^custom_/i, ''));
        aliasTokens.forEach((token) => {
            if (!token) {
                return;
            }
            if (labelToken === token || keyToken === token) {
                scored.push({ field, score: 1000 + token.length });
                return;
            }
            if (labelToken.includes(token) || token.includes(labelToken)) {
                scored.push({ field, score: token.length });
            }
        });
    });
    if (scored.length === 0) {
        return null;
    }
    scored.sort((a, b) => b.score - a.score);
    return scored[0].field;
};

export const buildDefaultAsRepairListExportColumns = (mode) => (
    (DEFAULT_HEADERS_BY_MODE[mode] || []).map((header) => ({ header, fieldKey: '' }))
);

export const buildDefaultAsRepairListExportConfig = () => ({
    internal: buildDefaultAsRepairListExportColumns('internal'),
    external: buildDefaultAsRepairListExportColumns('external'),
});

const normalizeColumns = (raw, mode) => {
    if (!Array.isArray(raw)) {
        return buildDefaultAsRepairListExportColumns(mode);
    }
    return raw
        .map((col) => {
            const header = String((typeof col === 'string' ? col : col?.header) ?? '').trim();
            if (!header) {
                return null;
            }
            return {
                header,
                fieldKey: typeof col === 'string' ? '' : String(col?.fieldKey ?? '').trim(),
            };
        })
        .filter(Boolean);
};

export const normalizeAsRepairListExportConfig = (raw) => ({
    internal: normalizeColumns(raw?.internal, 'internal'),
    external: normalizeColumns(raw?.external, 'external'),
});

/**
 * 열 → 실제 A/S 필드. 연결 필드를 지정한 열은 그 필드, 비워 둔 열은 제목으로 자동 매칭.
 * 같은 필드가 자동 매칭으로 두 번 쓰이지 않도록 지정 필드부터 예약한다.
 */
export const resolveAsRepairListExportColumns = (fields, columns) => {
    const list = Array.isArray(fields) ? fields : [];
    const byKey = new Map(list.map((f) => [f.key, f]));
    const usedKeys = new Set();
    (columns || []).forEach((col) => {
        if (col.fieldKey && byKey.has(col.fieldKey)) {
            usedKeys.add(col.fieldKey);
        }
    });
    return (columns || []).map((col) => {
        if (col.fieldKey) {
            return { header: col.header, explicit: true, field: byKey.get(col.fieldKey) || null };
        }
        const field = findAsRepairFieldByExportHeader(list, col.header, usedKeys);
        if (field?.key) {
            usedKeys.add(field.key);
        }
        return { header: col.header, explicit: false, field };
    });
};
